import { CaretRight } from '@phosphor-icons/react';
import type { WorkbenchCard } from '@mybusinessmate/schema';
import { cx } from '../../lib/cx';
import { TASK_TYPE_META } from '../../lib/task-display';
import { dueText, groupQueue, laneOf, unitsText, versionPill, waitingLine } from '../../lib/workbench-display';
import { Icon } from '../Icon';
import { ProgressBar } from '../ui/progress-bar';
import { StatusTag } from '../ui/status-tag';
import { PriorityPill } from './priority-pill';

/** Figma A05.7c "Row": type tile, title and where it is, priority, status, progress, due, units, chevron. */
const GRID =
  'grid items-center gap-(--mbm-space-4) px-(--mbm-space-6) grid-cols-[var(--mbm-size-queue-icon-box)_minmax(0,1fr)_auto] lg:grid-cols-[var(--mbm-size-queue-icon-box)_minmax(0,472fr)_minmax(0,84fr)_minmax(0,160fr)_minmax(0,130fr)_minmax(0,190fr)_minmax(0,32fr)_auto]';

/** The first sentence of a note, cut short, as the design's "Client: “Music feels too slow…”". */
function quoteOf(note: string): string {
  const first = note
    .split(/(?<=[.?!])\s/)[0]!
    .trim()
    .replace(/[.?!]$/, '');
  const short = first.length > 28 ? `${first.slice(0, 28).trimEnd()}` : first;
  return short.length < note.trim().length ? `${short}…` : short;
}

/** "Paradise Biryani · Reel · v2 · Client: “Music feels too slow…”" */
function rowSubtitle(card: WorkbenchCard): string {
  const note = card.sentBack?.note;
  const quote =
    note && card.sentBack ? `${card.sentBack.by === 'client' ? 'Client' : 'Lead'}: “${quoteOf(note)}”` : null;
  return [card.businessName ?? card.projectName, TASK_TYPE_META[card.taskType].label, versionPill(card), quote]
    .filter(Boolean)
    .join(' · ');
}

function Row({
  card,
  viewerId,
  selected,
  onOpen,
  now,
}: {
  card: WorkbenchCard;
  viewerId: string | undefined;
  selected: boolean;
  onOpen: () => void;
  now?: Date;
}) {
  const lane = laneOf(card.column);
  const waiting = waitingLine(card, viewerId, now);
  const due = dueText(card.deadline, now);
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${card.title}. Open a quick look`}
      aria-pressed={selected}
      className={cx(
        GRID,
        'w-full py-(--mbm-space-3) text-left focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)',
        selected && 'bg-surface-field',
      )}
    >
      <span className="flex size-(--mbm-size-queue-icon-box) items-center justify-center rounded-(--mbm-component-radius-icon-tile) bg-surface-field text-text-primary">
        <Icon icon={TASK_TYPE_META[card.taskType].icon} size="sm" />
      </span>
      <span className="flex min-w-0 flex-col gap-(--mbm-space-0-5)">
        <span className="truncate text-label-medium text-text-primary">{card.title}</span>
        <span className="truncate text-body-small text-text-secondary">{rowSubtitle(card)}</span>
      </span>
      <span className="hidden lg:block">
        <PriorityPill priority={card.priority} />
      </span>
      <span className="hidden lg:block">
        <StatusTag label={lane.title} tone={lane.tone} />
      </span>
      <span className="hidden items-center gap-(--mbm-space-2) lg:flex">
        <ProgressBar
          value={card.progress}
          tone={card.progress >= 100 ? 'success' : 'dark'}
          label={`${card.title} progress`}
          size="default"
        />
        <span className="w-(--mbm-space-6) shrink-0 text-label-small text-text-primary">{card.progress}%</span>
      </span>
      <span
        className={cx(
          'hidden truncate text-body-small lg:block',
          !waiting && due.urgent ? 'text-tag-amber-text' : waiting ? 'text-text-secondary' : 'text-text-secondary',
        )}
      >
        {waiting ?? due.text}
      </span>
      <span className="hidden text-label-small text-text-primary lg:block">{unitsText(card)}</span>
      <span className="text-text-primary">
        <Icon icon={CaretRight} size="sm" />
      </span>
    </button>
  );
}

/**
 * Figma A05.7c: the daily queue. The viewer's own work by day (today first), then what is waiting on others. The same
 * cards as the board, in the order to do them.
 */
export function QueueList({
  cards,
  viewerId,
  selectedId,
  onOpen,
  now,
}: {
  cards: WorkbenchCard[];
  viewerId: string | undefined;
  selectedId: string | null;
  onOpen: (card: WorkbenchCard) => void;
  now?: Date;
}) {
  const groups = groupQueue(cards, now);
  return (
    <div className="rounded-(--mbm-component-radius-card) bg-surface-card py-(--mbm-space-2)">
      {groups.map((group) => (
        <section key={group.id} aria-label={group.heading}>
          <h2 className="px-(--mbm-space-6) pt-(--mbm-space-4) pb-(--mbm-space-1-5) text-label-small text-text-tertiary uppercase">
            {group.heading}
          </h2>
          {group.items.map((card) => (
            <Row
              key={card.taskId}
              card={card}
              viewerId={viewerId}
              selected={selectedId === card.taskId}
              onOpen={() => onOpen(card)}
              now={now}
            />
          ))}
        </section>
      ))}
    </div>
  );
}
